import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Tabs,
  Input,
  Select,
  Tooltip,
  Modal,
  message,
  Typography,
  Empty,
  Result,
  Form,
  Alert,
} from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
  HistoryOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import apiService, {
  Lead,
  LeadFileReviewItem,
  LeadFileReviewStatus,
} from '../../services/api';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import { getUserRole } from '../../utils/cognitoUtils';
import { isAdmin } from '../../utils/roleUtils';
import {
  workspacePage,
  workspaceSurface,
  workspaceCardHead,
  workspaceCardBody,
  workspaceTableWrap,
} from '../ClientWorkspace/workspaceTheme';
import FileReviewModal, {
  REVIEW_STATUS_COLORS,
  REVIEW_STATUS_LABELS,
} from '../ClientWorkspace/FileReviewModal';
import VodafoneDocumentWizard from '../VodafoneDocumentWizard';
import NOSDocumentWizard from '../NOSDocumentWizard';
import NOSSecuritasDocumentWizard from '../NOSSecuritasDocumentWizard';
import ProsegurForm from '../ProsegurForm';
import AlticeResidencialForm from '../AlticeResidencialForm';
import { formatUtcApiDateTime } from '../../utils/formatDateTime';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS: { key: LeadFileReviewStatus | 'all'; label: string }[] = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'approved', label: 'Aprovados' },
  { key: 'rejected', label: 'Rejeitados' },
  { key: 'all', label: 'Todos' },
];

const BUSINESS_AREA_OPTIONS = [
  { value: '', label: 'Todas as áreas' },
  { value: 'telecommunications', label: 'Telecomunicações' },
  { value: 'energy_solutions', label: 'Energia' },
];

const formatFileSize = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isFormQueueRow = (row: LeadFileReviewItem): boolean =>
  row.item_type === 'form' || row.id.toString().startsWith('form-');

const FilesProcessing: React.FC = () => {
  const { user } = useCognitoAuth();
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  const [items, setItems] = useState<LeadFileReviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0 });

  const [statusTab, setStatusTab] = useState<LeadFileReviewStatus | 'all'>('pending');
  const [search, setSearch] = useState('');
  const [businessArea, setBusinessArea] = useState<string>('');
  const searchTimer = useRef<number | null>(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [reviewTarget, setReviewTarget] = useState<LeadFileReviewItem | null>(null);

  // Bulk action modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject'>('approve');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const [formsLead, setFormsLead] = useState<Lead | null>(null);
  const [formsLeadLoading, setFormsLeadLoading] = useState(false);
  const [vodafoneOpen, setVodafoneOpen] = useState(false);
  const [nosOpen, setNosOpen] = useState(false);
  const [nosSecuritasOpen, setNosSecuritasOpen] = useState(false);
  const [prosegurOpen, setProsegurOpen] = useState(false);
  const [alticeOpen, setAlticeOpen] = useState(false);
  const [singleDecisionOpen, setSingleDecisionOpen] = useState(false);
  const [singleDecisionAction, setSingleDecisionAction] = useState<'approve' | 'reject'>('approve');
  const [singleDecisionItem, setSingleDecisionItem] = useState<LeadFileReviewItem | null>(null);
  const [singleDecisionNote, setSingleDecisionNote] = useState('');
  const [singleDecisionReason, setSingleDecisionReason] = useState('');
  const [singleDecisionSubmitting, setSingleDecisionSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    if (user && isAdmin(getUserRole(user))) {
      setAllowed(true);
      setPermissionLoading(false);
      return () => {
        active = false;
      };
    }
    apiService
      .getFileReviewPermissions()
      .then((resp) => {
        if (!active) return;
        setAllowed(Boolean(resp.success && resp.can_review_files));
      })
      .finally(() => {
        if (active) setPermissionLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const loadQueue = useCallback(
    async (overrides?: { page?: number; pageSize?: number }) => {
      if (!allowed) return;
      setLoading(true);
      try {
        const page = overrides?.page ?? pagination.page;
        const pageSize = overrides?.pageSize ?? pagination.pageSize;
        const resp = await apiService.getFilesReviewQueue({
          status: statusTab,
          search: search.trim() || undefined,
          business_area: businessArea || undefined,
          page,
          page_size: pageSize,
        });
        if (resp.success) {
          setItems(resp.items || []);
          setCounts(resp.counts || {});
          setPagination({
            page: resp.pagination.page,
            pageSize: resp.pagination.page_size,
            total: resp.pagination.total,
          });
        } else {
          message.error(resp.message || 'Erro ao carregar fila de revisão.');
        }
      } finally {
        setLoading(false);
      }
    },
    [allowed, statusTab, search, businessArea, pagination.page, pagination.pageSize]
  );

  // initial + filter-driven loads (reset to page 1 when filters change)
  useEffect(() => {
    if (!allowed) return;
    setSelectedRowKeys([]);
    loadQueue({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, statusTab, businessArea]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimer.current) {
      window.clearTimeout(searchTimer.current);
    }
    searchTimer.current = window.setTimeout(() => {
      loadQueue({ page: 1 });
    }, 350);
  };

  const handleSingleReviewed = (updated: LeadFileReviewItem) => {
    setItems((prev) =>
      prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
    );
    loadQueue();
  };

  const openBulkModal = (action: 'approve' | 'reject') => {
    if (selectedRowKeys.length === 0) {
      message.info('Selecione pelo menos um ficheiro.');
      return;
    }
    setBulkAction(action);
    setBulkNote('');
    setBulkReason('');
    setBulkOpen(true);
  };

  const handleBulkSubmit = async () => {
    const selectedItems = items.filter((it) => selectedRowKeys.includes(it.id));
    const selectedFiles = selectedItems.filter((it) => !isFormQueueRow(it));
    const selectedForms = selectedItems.filter((it) => isFormQueueRow(it));
    const fileIds = selectedFiles.map((it) => Number(it.id)).filter((n) => Number.isFinite(n));

    if (selectedItems.length === 0) {
      setBulkOpen(false);
      return;
    }
    if (bulkAction === 'reject' && !bulkReason.trim()) {
      message.error('Indique o motivo da rejeição.');
      return;
    }
    setBulkSubmitting(true);
    try {
      let okCount = 0;
      let failCount = 0;

      if (fileIds.length > 0) {
        const resp = await apiService.reviewLeadFilesBulk({
          file_ids: fileIds,
          action: bulkAction,
          note: bulkNote.trim() || undefined,
          rejection_reason: bulkAction === 'reject' ? bulkReason.trim() : undefined,
        });
        okCount += resp.processed || 0;
        failCount += (resp.requested || 0) - (resp.processed || 0);
      }

      if (selectedForms.length > 0) {
        const formResults = await Promise.all(
          selectedForms.map((row) =>
            apiService.reviewLeadForm(row.lead_id, row.form_type || '', {
              action: bulkAction,
              note: bulkNote.trim() || undefined,
              rejection_reason: bulkAction === 'reject' ? bulkReason.trim() : undefined,
            })
          )
        );
        formResults.forEach((r) => {
          if (r.success) okCount += 1;
          else failCount += 1;
        });
      }

      if (okCount > 0) {
        if (failCount === 0) {
          message.success(`${okCount} item(ns) processados.`);
        } else {
          message.warning(`${okCount} processados, ${failCount} com erro.`);
        }
      } else {
        message.error('Não foi possível processar a seleção.');
      }
      setBulkOpen(false);
      setSelectedRowKeys([]);
      loadQueue();
    } finally {
      setBulkSubmitting(false);
    }
  };

  const openSingleDecision = (row: LeadFileReviewItem, action: 'approve' | 'reject') => {
    setSingleDecisionItem(row);
    setSingleDecisionAction(action);
    setSingleDecisionNote('');
    setSingleDecisionReason('');
    setSingleDecisionOpen(true);
  };

  const handleSingleDecisionSubmit = async () => {
    if (!singleDecisionItem) return;
    if (singleDecisionAction === 'reject' && !singleDecisionReason.trim()) {
      message.error('Indique o motivo da rejeição.');
      return;
    }
    setSingleDecisionSubmitting(true);
    try {
      if (isFormQueueRow(singleDecisionItem)) {
        const resp = await apiService.reviewLeadForm(singleDecisionItem.lead_id, singleDecisionItem.form_type || '', {
          action: singleDecisionAction,
          note: singleDecisionNote.trim() || undefined,
          rejection_reason: singleDecisionAction === 'reject' ? singleDecisionReason.trim() : undefined,
        });
        if (!resp.success) {
          message.error(resp.message || 'Erro ao registar decisão do formulário.');
          return;
        }
      } else {
        const fileId = Number(singleDecisionItem.id);
        const resp = await apiService.reviewLeadFile(singleDecisionItem.lead_id, fileId, {
          action: singleDecisionAction,
          note: singleDecisionNote.trim() || undefined,
          rejection_reason: singleDecisionAction === 'reject' ? singleDecisionReason.trim() : undefined,
        });
        if (!resp.success) {
          message.error(resp.message || 'Erro ao registar decisão do ficheiro.');
          return;
        }
      }
      message.success(singleDecisionAction === 'approve' ? 'Aprovado.' : 'Rejeitado.');
      setSingleDecisionOpen(false);
      setSingleDecisionItem(null);
      loadQueue();
    } finally {
      setSingleDecisionSubmitting(false);
    }
  };

  const detectFormTypeFromRow = (row: LeadFileReviewItem): 'vodafone' | 'nos' | 'nos_securitas' | 'prosegur' | 'altice_meo' | null => {
    if (row.form_type) {
      const ft = row.form_type.toLowerCase();
      if (ft === 'vodafone' || ft === 'nos' || ft === 'nos_securitas' || ft === 'prosegur' || ft === 'altice_meo') {
        return ft;
      }
    }
    const source = `${row.file_name || ''} ${row.stage || ''} ${row.field_name || ''}`.toLowerCase();
    if (source.includes('altice') || source.includes('meo')) return 'altice_meo';
    if (source.includes('nos securitas') || source.includes('securitas')) return 'nos_securitas';
    if (source.includes('prosegur')) return 'prosegur';
    if (source.includes('vodafone')) return 'vodafone';
    if (source.includes('nos')) return 'nos';
    return null;
  };

  const openLeadFormForRow = async (row: LeadFileReviewItem) => {
    const formType = detectFormTypeFromRow(row);
    if (!formType) {
      message.warning('Não foi possível identificar automaticamente o formulário para este ficheiro.');
      return;
    }

    setFormsLeadLoading(true);
    try {
      const lead = await apiService.getLead(row.lead_id);
      setFormsLead(lead);
      if (formType === 'vodafone') setVodafoneOpen(true);
      if (formType === 'nos') setNosOpen(true);
      if (formType === 'nos_securitas') setNosSecuritasOpen(true);
      if (formType === 'prosegur') setProsegurOpen(true);
      if (formType === 'altice_meo') setAlticeOpen(true);
    } catch {
      message.error('Não foi possível carregar o lead para os formulários.');
    } finally {
      setFormsLeadLoading(false);
    }
  };

  const columns: ColumnsType<LeadFileReviewItem> = useMemo(
    () => [
      {
        title: 'Ficheiro',
        dataIndex: 'file_name',
        key: 'file_name',
        render: (_: string, row) => (
          <Space>
            <FileTextOutlined />
            <div>
              <div style={{ fontWeight: 600 }}>
                {row.form_label || row.file_name}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {[row.stage, row.field_name].filter(Boolean).join(' · ')}
                {!isFormQueueRow(row) && row.file_size ? ` · ${formatFileSize(row.file_size)}` : ''}
              </div>
            </div>
          </Space>
        ),
      },
      {
        title: 'Cliente',
        dataIndex: ['lead', 'name'],
        key: 'lead',
        render: (_: unknown, row) => (
          <div>
            <div style={{ fontWeight: 600 }}>{row.lead?.name || `Lead #${row.lead_id}`}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {[row.lead?.email, row.lead?.phone].filter(Boolean).join(' · ')}
            </div>
          </div>
        ),
      },
      {
        title: 'Carregado',
        dataIndex: 'uploaded_at',
        key: 'uploaded_at',
        width: 260,
        render: (value: string, row) => (
          <div>
            <div>{formatUtcApiDateTime(value)}</div>
            {(row.user_name || row.lead?.created_user_name || row.lead?.assigned_user_name) ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>
                por {row.user_name || row.lead?.created_user_name || row.lead?.assigned_user_name}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Estado',
        dataIndex: 'review_status',
        key: 'review_status',
        width: 130,
        render: (value: LeadFileReviewStatus, row) => (
          <Space direction="vertical" size={2} style={{ display: 'inline-flex' }}>
            <Tag color={REVIEW_STATUS_COLORS[value] || 'default'}>
              {REVIEW_STATUS_LABELS[value] || value}
            </Tag>
            {row.reviewed_at ? (
              <span style={{ fontSize: 11, color: '#64748b' }}>
                {formatUtcApiDateTime(row.reviewed_at)}
                {row.reviewer_name ? ` · ${row.reviewer_name}` : ''}
              </span>
            ) : null}
          </Space>
        ),
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 320,
        render: (_: unknown, row) => (
          <Space>
            <Tooltip title="Rever ficheiro">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  if (isFormQueueRow(row)) {
                    openLeadFormForRow(row);
                    return;
                  }
                  setReviewTarget(row);
                }}
              />
            </Tooltip>
            <Tooltip title="Abrir formulário relacionado">
              <Button
                size="small"
                icon={<AppstoreOutlined />}
                loading={formsLeadLoading}
                onClick={() => openLeadFormForRow(row)}
                disabled={!isFormQueueRow(row) && !detectFormTypeFromRow(row)}
              />
            </Tooltip>
            {row.review_status === 'pending' ? (
              <>
                <Tooltip title="Aprovar">
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => openSingleDecision(row, 'approve')}
                  />
                </Tooltip>
                <Tooltip title="Rejeitar">
                  <Button
                    size="small"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => openSingleDecision(row, 'reject')}
                  />
                </Tooltip>
              </>
            ) : null}
          </Space>
        ),
      },
    ],
    []
  );

  const rowSelection: TableRowSelection<LeadFileReviewItem> = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (row) => ({
      // bulk decisions only make sense for pending uploaded files
      disabled: row.review_status !== 'pending',
    }),
  };

  if (permissionLoading) {
    return (
      <div style={workspacePage}>
        <Card style={workspaceSurface} loading bodyStyle={workspaceCardBody} />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div style={workspacePage}>
        <Card style={workspaceSurface} bodyStyle={workspaceCardBody}>
          <Result
            status="403"
            title="Acesso restrito"
            subTitle="Não tem permissão para processar ficheiros de clientes. Contacte um administrador."
          />
        </Card>
      </div>
    );
  }

  const tabItems = STATUS_OPTIONS.map((opt) => ({
    key: opt.key,
    label: (
      <span>
        {opt.label}
        {typeof counts[opt.key] === 'number' ? (
          <Tag style={{ marginLeft: 6 }}>{counts[opt.key]}</Tag>
        ) : null}
      </span>
    ),
  }));

  return (
    <div style={workspacePage}>
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Processamento de Ficheiros
            </Title>
          </Space>
        }
        style={workspaceSurface}
        headStyle={workspaceCardHead}
        bodyStyle={{ ...workspaceCardBody, padding: '16px 20px 20px' }}
        extra={
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Procurar ficheiro, cliente, email…"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ minWidth: 260 }}
            />
            <Select
              value={businessArea}
              onChange={setBusinessArea}
              options={BUSINESS_AREA_OPTIONS}
              style={{ minWidth: 180 }}
            />
            <Tooltip title="Atualizar">
              <Button icon={<ReloadOutlined />} onClick={() => loadQueue()} loading={loading} />
            </Tooltip>
          </Space>
        }
      >
        <Tabs
          activeKey={statusTab}
          onChange={(key) => setStatusTab(key as LeadFileReviewStatus | 'all')}
          items={tabItems}
        />

        {selectedRowKeys.length > 0 ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${selectedRowKeys.length} item(ns) selecionado(s)`}
            action={
              <Space>
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => openBulkModal('approve')}
                >
                  Aprovar selecionados
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => openBulkModal('reject')}
                >
                  Rejeitar selecionados
                </Button>
                <Button size="small" onClick={() => setSelectedRowKeys([])}>
                  Limpar
                </Button>
              </Space>
            }
          />
        ) : null}

        <div style={workspaceTableWrap}>
          <Table<LeadFileReviewItem>
            rowKey="id"
            columns={columns}
            dataSource={items}
            loading={loading}
            rowSelection={rowSelection}
            size="middle"
            locale={{
              emptyText: (
                <Empty
                  description={
                    <Text type="secondary">
                      Sem ficheiros para mostrar nesta categoria.
                    </Text>
                  }
                />
              ),
            }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination((prev) => ({ ...prev, page, pageSize: pageSize || prev.pageSize }));
                loadQueue({ page, pageSize });
              },
            }}
          />
        </div>
      </Card>

      <FileReviewModal
        open={Boolean(reviewTarget)}
        file={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onReviewed={handleSingleReviewed}
      />

      <Modal
        open={bulkOpen}
        onCancel={() => setBulkOpen(false)}
        title={bulkAction === 'approve' ? 'Aprovar ficheiros selecionados' : 'Rejeitar ficheiros selecionados'}
        onOk={handleBulkSubmit}
        okText={bulkAction === 'approve' ? 'Aprovar tudo' : 'Rejeitar tudo'}
        okButtonProps={{ danger: bulkAction === 'reject', loading: bulkSubmitting }}
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label={`Itens: ${selectedRowKeys.length}`} style={{ marginBottom: 12 }}>
            <Text type="secondary">
              A decisão será aplicada aos ficheiros e formulários selecionados.
            </Text>
          </Form.Item>
          {bulkAction === 'reject' ? (
            <Form.Item label="Motivo da rejeição" required>
              <TextArea
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            </Form.Item>
          ) : null}
          <Form.Item label="Nota adicional (opcional)">
            <TextArea
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={singleDecisionOpen}
        onCancel={() => setSingleDecisionOpen(false)}
        title={singleDecisionAction === 'approve' ? 'Aprovar item' : 'Rejeitar item'}
        onOk={handleSingleDecisionSubmit}
        okText={singleDecisionAction === 'approve' ? 'Aprovar' : 'Rejeitar'}
        okButtonProps={{ danger: singleDecisionAction === 'reject', loading: singleDecisionSubmitting }}
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Item">
            <Text>{singleDecisionItem?.form_label || singleDecisionItem?.file_name}</Text>
          </Form.Item>
          {singleDecisionAction === 'reject' ? (
            <Form.Item label="Motivo da rejeição" required>
              <TextArea
                value={singleDecisionReason}
                onChange={(e) => setSingleDecisionReason(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            </Form.Item>
          ) : null}
          <Form.Item label="Nota adicional (opcional)">
            <TextArea
              value={singleDecisionNote}
              onChange={(e) => setSingleDecisionNote(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <VodafoneDocumentWizard
        open={vodafoneOpen}
        lead={formsLead}
        onClose={() => setVodafoneOpen(false)}
        onSave={() => {
          message.success('Documentos Vodafone guardados.');
          loadQueue();
        }}
      />
      <NOSDocumentWizard
        open={nosOpen}
        lead={formsLead}
        onClose={() => setNosOpen(false)}
        onSave={() => {
          message.success('Documentos NOS guardados.');
          loadQueue();
        }}
      />
      <NOSSecuritasDocumentWizard
        open={nosSecuritasOpen}
        lead={formsLead}
        onClose={() => setNosSecuritasOpen(false)}
        onSave={() => {
          message.success('Documentos NOS Securitas guardados.');
          loadQueue();
        }}
      />
      <ProsegurForm
        open={prosegurOpen}
        lead={formsLead}
        onClose={() => setProsegurOpen(false)}
        onSave={() => {
          message.success('Dados Prosegur guardados.');
          loadQueue();
        }}
      />
      <Modal
        title={`MEO — ${formsLead?.name || ''}`}
        open={alticeOpen}
        onCancel={() => setAlticeOpen(false)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        destroyOnClose
      >
        {formsLead ? (
          <AlticeResidencialForm
            lead={{
              id: formsLead.id,
              name: formsLead.name,
              email: formsLead.email,
              phone: formsLead.phone,
              address: formsLead.address,
              city: formsLead.city,
              postal_code: formsLead.postal_code,
              dif: formsLead.dif,
            }}
            onClose={() => setAlticeOpen(false)}
            onSave={() => {
              loadQueue();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default FilesProcessing;
